import { PlayIcon } from '@heroicons/react/24/outline';
import { Button, timestampFormatter } from '@retainerclub/common';
import { useLocale } from '@retainerclub/hooks';
import PropTypes from 'prop-types';
import SubscriptionActionAlert from '../ManageSubscriptionConfirmation/SubscriptionActionAlert';

function ManageSubscriptionUnpause({
  subscription,
  handleUnpauseAction,
  handleCloseModal,
  actionError,
}) {
  const { locale } = useLocale();

  function formatPauseEndDate() {
    if (subscription.pauseEndAt) {
      return timestampFormatter(subscription.pauseEndAt, false);
    }
    return 'Unknown';
  }

  function formatPauseDuration() {
    if (subscription.pauseDuration) {
      const months = Math.round(subscription.pauseDuration / 30);
      return `${months} month${months > 1 ? 's' : ''}`;
    }
    return 'Unknown duration';
  }

  return (
    <div className="space-y-6">
      {/* Header with Icon */}
      <div className="text-center">
        <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-rcprimary-100">
          <PlayIcon className="h-8 w-8 text-rcprimary-400" />
        </div>
        <h3 className="mt-4 font-semibold text-gray-900 text-lg">
          Resume Your Subscription
        </h3>
        <p className="mt-2 text-gray-600 text-sm">
          Your subscription is currently paused. You can resume it at any time.
        </p>
      </div>

      {/* Current Pause Status */}
      <div className="rounded-lg border border-yellow-200 bg-yellow-50 p-4">
        <h4 className="mb-3 font-medium text-yellow-900">
          Current Pause Status
        </h4>
        <div className="space-y-2 text-sm text-yellow-800">
          <div className="flex justify-between">
            <span>Pause Duration:</span>
            <span className="font-medium">{formatPauseDuration()}</span>
          </div>
          <div className="flex justify-between">
            <span>Current Pause End Date &amp; Renewal:</span>
            <span className="font-medium">{formatPauseEndDate()}</span>
          </div>
        </div>
      </div>

      {/* What Happens When You Resume */}
      <div className="rounded-lg border border-green-200 bg-green-50 p-4">
        <h4 className="mb-3 font-medium text-green-900">
          What happens when you resume?
        </h4>
        <ul className="space-y-2 text-green-800 text-sm">
          <li className="flex items-start">
            <span className="mr-2">•</span>
            <span>Your subscription will immediately become active again</span>
          </li>
          <li className="flex items-start">
            <span className="mr-2">•</span>
            <span>
              Your upcoming retainer shipments will be adjusted to refelct your
              reactivation
            </span>
          </li>
          <li className="flex items-start">
            <span className="mr-2">•</span>
            <span>
              Billing will resume on your new renewal date of today,
              {new Date().toLocaleDateString(locale)}
            </span>
          </li>
          <li className="flex items-start">
            <span className="mr-2">•</span>
            <span>You can pause again at any time if needed</span>
          </li>
        </ul>
      </div>

      {/* Subscription Details */}
      <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
        <h4 className="mb-3 font-medium text-gray-900">Subscription Details</h4>
        <div className="space-y-2 text-gray-700 text-sm">
          <div className="flex justify-between">
            <span>Plan:</span>
            <span className="font-medium">{subscription.name}</span>
          </div>
          <div className="flex justify-between">
            <span>Price:</span>
            <span className="font-medium">
              {(subscription.subscriptionPrice / 100).toLocaleString(locale, {
                style: 'currency',
                currency: 'USD',
              })}{' '}
              per renewal
            </span>
          </div>
          <div className="flex justify-between">
            <span>Patient:</span>
            <span className="font-medium">
              {subscription.patient.firstName} {subscription.patient.lastName}
            </span>
          </div>
        </div>
      </div>

      <SubscriptionActionAlert actionError={actionError} />

      {/* Action Buttons */}
      <div className="flex space-x-4">
        <Button
          name="Keep Paused"
          size="lg"
          rounded
          color="grayOutline"
          className="flex-1 justify-center"
          onClick={handleCloseModal}
        />
        <Button
          name="Resume Subscription"
          size="lg"
          rounded
          color="green"
          onClick={handleUnpauseAction}
          className="flex-1 justify-center"
        />
      </div>

      {/* Additional Information */}
      <div className="rounded-lg border border-blue-200 bg-blue-50 p-4">
        <h4 className="mb-2 font-medium text-blue-900">Need Help?</h4>
        <p className="text-blue-800 text-sm">
          If you have any questions about resuming your subscription or need
          assistance, please contact our{' '}
          <a
            href="https://www.retainerclub.com/support"
            target="_blank"
            rel="noopener noreferrer"
            className="text-blue-800 underline"
          >
            support team
          </a>
          . We're here to help!
        </p>
      </div>
    </div>
  );
}

ManageSubscriptionUnpause.propTypes = {
  subscription: PropTypes.object.isRequired,
  handleUnpauseAction: PropTypes.func.isRequired,
  handleCloseModal: PropTypes.func.isRequired,
  actionError: PropTypes.object,
};

export default ManageSubscriptionUnpause;
